import React from 'react';
import { useTranslation } from 'react-i18next';

const CurrentSchedule = ({ authUser, schedule, fetchedCourseData }) => {
  const { t } = useTranslation();

  const calculateTotalCredits = (schedule) => {
    const allClasses = Object.values(schedule).flat();
    const filteredCourses = fetchedCourseData.filter((course) =>
      allClasses.includes(course.courseInfo_courseNumber)
    );
    const totalCredits = filteredCourses.reduce((sum, course) => {
      return sum + parseInt(course.Credits, 10);
    }, 0);
    return totalCredits;
  };

  const totalCredits = calculateTotalCredits(schedule);
  const currentYear = authUser?.surveyAnswers?.Year || t('currentSchedule.na', 'N/A');

  // Helper to render a course entry. If the course is an object (placeholder),
  // then render a custom string.
  const renderCourse = (course) => {
    if (course && typeof course === 'object') {
      // For placeholders, we assume the courseInfo_courseNumber is "Placeholder"
      if (course.courseInfo_courseNumber === 'Placeholder') {
        return `Placeholder (${course.PlaceholderID}) - ${course.courseInfo_courseName}`;
      }
      // Otherwise, format the course object (adjust as needed)
      return `${course.courseInfo_courseNumber}: ${course.courseInfo_courseName}`;
    }
    // If it's already a string, return it directly
    return course;
  };

  return (
    <div className="bg-gray-50 p-3 rounded-lg shadow-md border border-gray-200">
      <h2 className="text-gray-700 font-semibold">
        {t('currentSchedule.title', {
          year: currentYear,
          credits: totalCredits,
          defaultValue: 'Current Schedule ({{year}}) - Total Credits: {{credits}}',
        })}
      </h2>

      {Object.keys(schedule).length > 0 ? (
        <div className="flex justify-between">
          {/* Fall Semester Section */}
          <div className="w-1/2 pr-1" style={{ maxWidth: 'calc(50%)' }}>
            <h3 className="text-lg text-gray-500 font-semibold mt-2">
              {t('currentSchedule.fallSemester', 'Fall Semester')}
            </h3>
            <ul>
              {schedule.Fall && schedule.Fall.length > 0 ? (
                schedule.Fall.map((course, index) => (
                  <li
                    key={index}
                    className="bg-blue-200 text-blue-800 text-sm font-medium me-2 px-2.5 py-1 rounded dark:bg-blue-700 dark:text-red-400 border border-red-400 mb-2 inline-block"
                  >
                    {renderCourse(course)}
                  </li>
                ))
              ) : (
                <span className="bg-red-100 text-red-800 text-sm font-medium me-2 px-2.5 py-1 rounded dark:bg-red-900 dark:text-red-300">
                  {t('currentSchedule.noFallCourses', 'No Planned Courses for Fall Semester')}
                </span>
              )}
            </ul>
          </div>

          {/* Spring Semester Section */}
          <div className="w-1/2 pl-1" style={{ maxWidth: 'calc(50%)' }}>
            <h3 className="text-lg text-gray-500 font-semibold mt-2">
              {t('currentSchedule.springSemester', 'Spring Semester')}
            </h3>
            <ul>
              {schedule.Spring && schedule.Spring.length > 0 ? (
                schedule.Spring.map((course, index) => (
                  <li
                    key={index}
                    className="bg-blue-200 text-blue-800 text-sm font-medium me-2 px-2.5 py-1 rounded dark:bg-blue-700 dark:text-red-400 border border-red-400 mb-2 inline-block"
                  >
                    {renderCourse(course)}
                  </li>
                ))
              ) : (
                <span className="bg-red-100 text-red-800 text-sm font-medium me-2 px-2.5 py-1 rounded dark:bg-red-900 dark:text-red-300">
                  {t('currentSchedule.noSpringCourses', 'No Planned Courses for Spring Semester')}
                </span>
              )}
            </ul>
          </div>
        </div>
      ) : (
        <span className="bg-red-100 text-red-800 text-sm font-medium me-2 px-2.5 py-0.5 rounded dark:bg-red-900 dark:text-red-300">
          {t('currentSchedule.noCurrentYearCourses', 'No Planned Courses for Current Year')}
        </span>
      )}
    </div>
  );
};

export default CurrentSchedule;
